import React from 'react'
import styled from '@emotion/styled'
import {SearchFormDesktop} from 'components/SearchBox/Home/SearchFormDesktop'
import {SearchFormMobile} from 'components/SearchBox/Home/SearchFormMobile'

import {useDeviceLayout} from '@vio/atlas/context/deviceLayout'
import {Box} from '@vio/atlas/helpers/Box'
import {
  PRIMARY_ROW_MIN_HEIGHT_DESKTOP_XS,
  PRIMARY_ROW_MIN_HEIGHT_MOBILE
} from '@vio/atlas/NavBar'
import {PageWidthContainer} from '@vio/atlas/src/components/designSystem/helpers/PageWidthContainer'
import {Text} from '@vio/atlas/Text'
import {cssTheme} from '@vio/atlas/themes'
import {linariaMq} from '@vio/atlas/utils/breakpoints'
import {useBrand} from '@vio/core/src/_web/brand/hooks/useBrand'
import {useBrandCapabilities} from '@vio/core/src/_web/brand/hooks/useBrandCapabilities'
import FormattedMessageWrapper from '@vio/core/src/localization/components/FormattedMessage'

import {TrustpilotDetails} from '../TrustpilotDetails'
import {AlternativeDesktopSearchForm} from './AlternativeDesktopSearchForm'
import {SocialProof} from './SocialProof'

const TABLET_HEIGHT = 631
const DESKTOP_HEIGHT = 560

const Wrapper = styled.section<{
  backgroundImage?: string
  hasGradient: boolean
}>`
  position: relative;
  background: ${({hasGradient}) =>
    hasGradient
      ? `linear-gradient(${cssTheme.colors.neutral.c000}, ${cssTheme.colors.background.brand.secondary})`
      : cssTheme.colors.background.neutral.secondary};
  padding-top: ${PRIMARY_ROW_MIN_HEIGHT_MOBILE};

  ${linariaMq.desktopXs} {
    padding-top: ${PRIMARY_ROW_MIN_HEIGHT_DESKTOP_XS};
    height: ${TABLET_HEIGHT}px;
  }
  ${linariaMq.desktopSm} {
    height: ${DESKTOP_HEIGHT}px;
  }

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-image: ${({backgroundImage}) =>
      backgroundImage ? `url('${backgroundImage}')` : 'none'};
    background-repeat: no-repeat;
    background-position-y: 100px;
    background-size: contain;

    ${linariaMq.desktopXs} {
      background-position-y: 200px;
    }

    ${linariaMq.desktopSm} {
      background-size: cover;
      background-position-y: 90px;
    }

    [dir='rtl'] & {
      transform: scaleX(-1);
    }
  }
  .--social-proof-wrapper {
    width: 100%;
  }
`

const InnerWrapper = styled(PageWidthContainer)<{isCentered?: boolean}>`
  display: flex;
  flex-direction: column;
  justify-content: ${({isCentered}) =>
    isCentered ? 'center' : 'space-between'};
  align-items: flex-start;
  z-index: 2;
  position: relative;
  height: 100%;
  padding-top: ${({isCentered}) => (isCentered ? 0 : '80px')};

  padding-left: ${cssTheme.layout.spacing.s500};
  padding-right: ${cssTheme.layout.spacing.s500};
`

const SearchFormMobileWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  position: relative;
  padding: 0 ${cssTheme.layout.spacing.s400};

  ${linariaMq.desktopXs} {
    padding: 0;
  }

  & > div {
    width: inherit;
    padding: ${cssTheme.layout.spacing.s400};
    border-radius: ${cssTheme.layout.radius.xl};
    box-shadow: ${cssTheme.shadows.floating};
  }
`

const SearchFormDesktopWrapper = styled.div`
  .--search-form-horizontal-container {
    align-items: center;
    justify-content: center;
    position: relative;
  }

  .--search-form-horizontal-grid {
    ${linariaMq.desktopSm} {
      width: 800px;
      background-color: ${cssTheme.colors.background.neutral.primary};
      border: solid 1px ${cssTheme.colors.border.neutral.secondary};
      box-shadow: ${cssTheme.shadows.floating};
    }
  }

  .--search-form-horizontal-grid {
    ${linariaMq.desktopSm} {
      box-shadow: ${cssTheme.shadows.floating};
    }
  }
`

const ImageWrapper = styled.div`
  position: absolute;
  bottom: 0;
  inset-inline-end: 77px;
  margin-bottom: -3px;
`

const BleedingImageClip = styled.div`
  position: absolute;
  inset: ${PRIMARY_ROW_MIN_HEIGHT_DESKTOP_XS} 0 0;
  overflow: hidden;

  img {
    position: absolute;
  }
`

const staticAsset = (file: string) =>
  `${process.env.NEXT_PUBLIC_ASSETS_ENDPOINT}static/${file}`

export const HeroSectionMarketing: React.FC = () => {
  const {brandHomepageHeadingMessage} = useBrand()
  const {shell} = useBrandCapabilities()
  const {
    hasHeroSocialProof,
    hasMarketingHeroGradient,
    marketingHeroBackgroundImage,
    marketingHeroImage
  } = shell
  const {isMobile, isDesktopXs} = useDeviceLayout()
  const backgroundImage = marketingHeroBackgroundImage
    ? staticAsset(marketingHeroBackgroundImage)
    : undefined

  const bleed = marketingHeroImage?.bleed
  const artwork = marketingHeroImage && (
    <img
      src={staticAsset(marketingHeroImage.src)}
      alt=""
      width={marketingHeroImage.width}
      height={marketingHeroImage.height}
      style={bleed && {top: -bleed.top, insetInlineEnd: -bleed.inlineEnd}}
    />
  )

  const title = brandHomepageHeadingMessage && (
    <FormattedMessageWrapper {...brandHomepageHeadingMessage} />
  )

  if (isMobile)
    return (
      <Wrapper
        data-id="HeroSection"
        backgroundImage={backgroundImage}
        hasGradient={hasMarketingHeroGradient}
      >
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="flex-start"
          gap="s700"
          zIndex="2"
          position="relative"
        >
          <Box paddingTop="s800" paddingLeft="s500" paddingRight="s500">
            <Box
              display="flex"
              flexDirection="column"
              justifyContent="center"
              gap="s200"
            >
              <Text
                variant="title2XL"
                dataId="HomeHeading"
                colorPath="content.brand.primary"
              >
                {title}
              </Text>
            </Box>
          </Box>
          <SearchFormMobileWrapper>
            <SearchFormMobile />
          </SearchFormMobileWrapper>
          {hasHeroSocialProof && (
            <Box paddingTop="s500" paddingBottom="s600">
              <TrustpilotDetails section="Hero" />
            </Box>
          )}
        </Box>
      </Wrapper>
    )

  if (!isMobile && shell.hasAlternativeSearchForm.enabled) {
    return <AlternativeDesktopSearchForm />
  }

  if (isDesktopXs)
    return (
      <Wrapper
        data-id="HeroSection"
        backgroundImage={backgroundImage}
        hasGradient={hasMarketingHeroGradient}
      >
        <InnerWrapper>
          <Box display="flex" flexDirection="column" gap="s800" width="100%">
            <Text
              align="left"
              variant="title5XL"
              dataId="HomeHeading"
              colorPath="content.brand.primary"
            >
              {title}
            </Text>

            <SearchFormMobileWrapper>
              <SearchFormMobile />
            </SearchFormMobileWrapper>
          </Box>

          {hasHeroSocialProof && (
            <SocialProof isVioBrand className="--social-proof-wrapper" />
          )}
        </InnerWrapper>
      </Wrapper>
    )

  return (
    <Wrapper
      data-id="HeroSection"
      backgroundImage={backgroundImage}
      hasGradient={hasMarketingHeroGradient}
    >
      <InnerWrapper isCentered={!hasHeroSocialProof}>
        <Box maxWidth="935px">
          <Box
            display="flex"
            flexDirection="column"
            justifyContent="flex-start"
            gap="s900"
          >
            <Text
              align="left"
              variant="title5XL"
              dataId="HomeHeading"
              colorPath="content.brand.primary"
            >
              {title}
            </Text>
            <SearchFormDesktopWrapper>
              <SearchFormDesktop />
            </SearchFormDesktopWrapper>
          </Box>
        </Box>
        {hasHeroSocialProof && <SocialProof isVioBrand />}
      </InnerWrapper>

      {artwork &&
        (bleed ? (
          <BleedingImageClip>{artwork}</BleedingImageClip>
        ) : (
          <ImageWrapper>{artwork}</ImageWrapper>
        ))}
    </Wrapper>
  )
}
